import React, { useEffect, useLayoutEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslate } from '@bynder/localization';
import { Button, Flex, ModalBase } from '@bynder/design-system';
import useQueryParams from 'packages/hooks/useQueryParams';
import filtersStorage from 'packages/hooks/filtersStorage';
import features from 'packages/configs/features';
import AuthorizationHelper from 'packages/helpers/AuthorizationHelper';
import { Filters } from '../Filters';
import { List } from '../List';
import useExport from '../hooks/useExport';
import { DeselectAll } from 'packages/variation-export/Header/DeselectAll';
import modalContainer from 'packages/common/modalContainer';
import { useDeferredClose } from 'packages/hooks/useDeferredClose';

const ExportModal = ({ isOpen, onClose, onContinue }) => {
    const [shouldRender, onCloseAnimationEnd] = useDeferredClose(isOpen);
    const { translate } = useTranslate();
    const location = useLocation();

    const { getAllQueryParams, restoreQueryParams, resetQueryParams } = useQueryParams();
    const { getFiltersFromStorage, setFiltersToStorage } = filtersStorage();

    const { selectedCount, selectedIds, setSelectedIds, selectAll } = useExport();

    const hasMultiVariationFeature = AuthorizationHelper.isFeatureAvailable(features.MULTI_VARIATION);

    const variationIds = useMemo(() => Object.keys(selectedIds), [selectedIds]);
    const prevSelectedIds = useMemo(() => selectedIds, [isOpen]);

    const handleClose = () => {
        onClose();
        setSelectedIds(prevSelectedIds);
    };

    useEffect(() => {
        if (!hasMultiVariationFeature && isOpen) {
            selectAll(true);
        }
    }, [isOpen]);

    useLayoutEffect(() => {
        const storedQueries = getFiltersFromStorage('download');

        if (storedQueries) {
            restoreQueryParams(storedQueries);
        }

        return () => resetQueryParams(['tab', 'export', 'search', 'groupBy', 'orderBy', 'view']);
    }, []);

    useEffect(() => {
        if (location.search.includes('tab=download') && location.search.includes('export=true')) {
            setFiltersToStorage('download', getAllQueryParams());
        }
    }, [getAllQueryParams()]);

    if (!shouldRender) {
        return null;
    }

    return (
        <ModalBase
            container={modalContainer}
            isOpen={isOpen}
            onCloseAnimationEnd={onCloseAnimationEnd}
            onClose={handleClose}
            size={!hasMultiVariationFeature ? 'medium' : 'fullscreen'}
        >
            {hasMultiVariationFeature && (
                <ModalBase.Header
                    backButton={{ onClick: handleClose }}
                    title={
                        <Flex gap="4" alignItems="center">
                            {selectedCount === 0 ? 'Select items for export' : `${selectedCount} selected`}
                            <DeselectAll />
                        </Flex>
                    }
                />
            )}
            <ModalBase.Content>
                <div id="export_modal_body">
                    <Filters />
                    <List />
                </div>
            </ModalBase.Content>
            <ModalBase.Footer
                actionPrimary={
                    <Button
                        variant="primary"
                        onClick={onContinue}
                        disabled={!variationIds.length}
                        title={translate('modal.export.save-selection')}
                    >
                        {translate('modal.export.save-selection')}
                    </Button>
                }
                actionSecondary={
                    <Button variant="secondary" onClick={handleClose} title={translate('modal.export.cancel')}>
                        {translate('modal.export.cancel')}
                    </Button>
                }
            />
        </ModalBase>
    );
};

export default React.memo(ExportModal);
